import { errorDescriptionFromResponse } from '../../utils/error';
import ClapprUtils from '../../utils/clappr';
import HighlightCapturePlugin from '../../../player/highlight-capture-plugin';

// angular.component definition for a modified boxcast-player that
// supports a highlight capture component.
var playerWithHighlightCapture = {
  bindings: {
    channelId: '<',
    options: '<',
    broadcast: '<',
    onOpenHighlightPreview: '=',
    onNewHighlightCaptured: '=',
    onOpenLowLatencyPreview: '=',
    parentVm: '<',
  },
  template: `
    <boxcast-player ng-if="!vm.noViewers && vm.__readyToLoadPlayer"
                    channel-id="vm.channelId"
                    options="vm.options"></boxcast-player>
    <div class="fixed-16-9" ng-if="vm.noViewers">
      <h3 style="color:#fff;display:flex;align-items:center;justify-content:center;padding:0 15px;">
        <div>
          <p>{{vm.noViewersSimulcastMessage()}}</p>
          <p>
            <button ng-if="vm.canUsePreview() && vm.broadcast.actual_codec != 'hevc'"
                    ng-click="vm.openLowLatencyPreview()"
                    analytics-on analytics-event="event-edit" analytics-category="preview" analytics-label=""
                    class="btn btn-block btn-default overflow-ellipse" style="text-align:left">
              <i class="fa fa-video-camera" style="width:20px"></i>
              <span>Show Preview</span>
            </button>
          </p>
          <p>
            <a class="btn btn-block btn-default overflow-ellipse" style="text-align:left;position:relative;padding-left:37px;" ng-if="vm.canLinkToSocial()" ng-href="{{vm.socialLink()}}" target="_blank">
              <img ng-src="/img/{{vm.socialIcon()}}" ng-if="vm.socialIcon()" style="position:absolute;top:0;left:0;height:32px;width:32px;">
              {{vm.socialText()}}
            </a>
          </p>
        </div>
      </h3>
    </div>
  `,
  controllerAs: 'vm',
  controller: PlayerWithHighlightCaptureController,
};

function PlayerWithHighlightCaptureController(
  $scope,
  $element,
  $attrs,
  $timeout,
  $rootScope,
  $sce,
  toaster,
  Highlight
) {
  'ngInject';

  const { account, user } = $rootScope;
  this.FeatureFlags = account.feature_flags;
  this.account = account;
  this.$sce = $sce;
  this.toaster = toaster;
  this.Highlight = Highlight;
  this.noViewers = this.FeatureFlags.no_viewers;

  /* the following are provided automagically by angular.component bindings...
  this.channelId = channelId;
  this.options = options;
  this.onOpenHighlightPreview = onOpenHighlightPreview;
  this.onNewHighlightCaptured = onNewHighlightCaptured;
  this.onOpenLowLatencyPreview = onOpenLowLatencyPreview;
  this.parentVm = parentVm;
  */

  const watchFn = () => {
    if (this.noViewers) return;
    if (!this.channelId) return;
    if (!this.options.ticketId) return;
    if (
      this.FeatureFlags.highlights &&
      this.broadcast.recording_requested !== false &&
      user.can('create', 'broadcast/*:highlight')
    ) {
      var origOnLoadPlayer = this.options.onLoadPlayer || function () {};
      this.options.onLoadPlayer = (player) => {
        this.clapprUtils = new ClapprUtils(player);
        origOnLoadPlayer(player);
      };
      this.options.clapprPlugins = [HighlightCapturePlugin];
      this.options.clapprConfig = {
        highlightCapturePlugin: {
          onPreview: (data) => $timeout(() => this.onHighlightCapturePreview(data), 0),
          onSave: (data) => $timeout(() => this.onHighlightCaptureSave(data), 0),
        },
      };
    }
    this.__readyToLoadPlayer = true;
  };
  $scope.$watch(() => this.channelId, watchFn);
  $scope.$watch(() => this.options, watchFn, true);

  this._unpackAndValidateHighlightData = (data) => {
    var highlight = {
      name: data.name,
      start_seconds:
        data.start_seconds || Math.floor(this.clapprUtils.getMediaAbsoluteTimeForTime(data.start)),
      stop_seconds:
        data.stop_seconds || Math.ceil(this.clapprUtils.getMediaAbsoluteTimeForTime(data.end)),
    };
    if (highlight.start_seconds === null || highlight.stop_seconds === null) {
      console.warn('error getting start/stop seconds', data, highlight);
      this.toaster.pop(
        'warning',
        'Unable to generate highlight. Please check the selected time and duration and then try again.'
      );
      return;
    }
    return highlight;
  };

  this.onHighlightCapturePreview = (data) => {
    var highlight = this._unpackAndValidateHighlightData(data);
    if (!highlight) return;
    this.onOpenHighlightPreview
      .bind(this.parentVm)(highlight, true)
      .result.then((result) => {
        console.log('saving with original data', data, 'and updates from preview', result);
        // User opted to save the highlight from within the preview
        this.onHighlightCaptureSave(angular.extend({}, data, result));
      });
  };

  this.onHighlightCaptureSave = (data) => {
    var highlight = this._unpackAndValidateHighlightData(data);
    if (!highlight) return;
    if (!highlight.name) {
      this.toaster.pop('warning', 'Please provide a name for the highlight before saving.');
      return;
    }
    this.Highlight.create({ broadcastId: this.broadcast.id }, highlight)
      .$promise.then(() => {
        this.onNewHighlightCaptured.bind(this.parentVm)();
        data.fnClose();
      })
      .catch((err) => {
        console.error(err);
        this.toaster.pop(
          'warning',
          `The highlight could not be created: ${errorDescriptionFromResponse(err)}`
        );
      });
  };

  this.socialSimulcastInfo = () => {
    if (
      !this.broadcast ||
      !this.broadcast.simulcast_configurations ||
      this.broadcast.simulcast_configurations.length != 1 ||
      !this.broadcast.simulcast_configurations[0]
    ) {
      return null;
    }
    const simulcast = this.broadcast.simulcast_configurations[0];
    const { destination, fb_feed_id, youtube_broadcast_id, share_url } = simulcast;
    if (destination == 'facebook' && fb_feed_id) {
      return {
        destination: 'facebook',
        link: `https://www.facebook.com/${fb_feed_id.replace(/^[^:]*:/, '')}/videos`,
        text: 'Watch on Facebook',
      };
    }
    if (destination == 'youtube' && youtube_broadcast_id) {
      return {
        destination: 'youtube',
        link: `https://www.youtube.com/live_event_analytics?v=${youtube_broadcast_id}`,
        text: 'YouTube Live Control Room',
      };
    }
    if (destination == 'twitter' && share_url) {
      return { destination: 'twitter', link: share_url, text: 'Watch on Twitter' };
    }
    return null;
  };

  this.socialIcon = () => {
    let info = this.socialSimulcastInfo() || {};
    if (info.destination == 'facebook') return 'icon_facebook@30px.png';
    if (info.destination == 'youtube') return 'icon_youtube@30px.png';
    if (info.destination == 'twitter') return 'icon_twitter@30px.png';
    return '';
  };

  this.canLinkToSocial = () => {
    return !!this.socialSimulcastInfo();
  };

  this.socialLink = () => {
    return this.$sce.trustAsResourceUrl(this.socialSimulcastInfo().link);
  };

  this.socialText = () => {
    return this.socialSimulcastInfo().text;
  };

  this.canUsePreview = () => {
    const { account } = this;
    // Low-latency preview is gated by feature flag PLUS browser-compatibility
    // Note that we're not feature-detecting Flash specifically (although we could)
    // because this allows us to advertise to desktop users who might only have
    // to install the flash plugin to get it to work.
    if (!account.extra_features.low_latency_preview) {
      return false;
    }
    if (navigator.userAgent.indexOf('iOS') >= 0 || navigator.userAgent.indexOf('Android') >= 0) {
      return false;
    }
    return !!(this.broadcast || {}).rtmp_preview_url;
  };

  this.noViewersSimulcastMessage = () => {
    let beVerb;
    let haveVerb;
    switch (this.broadcast.timeframe) {
      case 'past':
        beVerb = 'was';
        haveVerb = 'had';
        break;

      case 'preroll': // fall-through
      case 'future':
        beVerb = 'will be';
        haveVerb = 'has';
        break;

      default:
        beVerb = 'is being';
        haveVerb = 'has';
        break;
    }

    let destinations = _.map(this.broadcast.simulcast_configurations, (s) => {
      switch (s.destination) {
        case 'facebook':
          return 'Facebook';
          break;

        case 'youtube':
          return 'YouTube';
          break;

        case 'twitter':
          return 'Twitter';
          break;

        default:
          return 'an RTMP destination';
          break;
      }
    });
    if (_.countBy(destinations, (d) => d === 'an RTMP destination') > 1) {
      destinations = _.map(destinations, (d) =>
        d !== 'an RTMP destination' ? d : 'RTMP destinations'
      );
    }
    destinations = _.uniq(destinations).sort();
    if (destinations.length === 0) {
      // In theory this should never happen, but in case it does, we short-circuit something sensible.
      return `This broadcast ${beVerb} streamed, but ${haveVerb} no destination configured.`;
    } else if (destinations.length === 1) {
      destinations = destinations[0];
    } else if (destinations.length === 2) {
      let first, last;
      if (_.contains(destinations, 'an RTMP destination')) {
        destinations = _.without(destinations, 'an RTMP destination');
        first = destinations[0];
        last = 'an RTMP destination';
      } else if (_.contains(destinations, 'RTMP destinations')) {
        destinations = _.without(destinations, 'RTMP destinations');
        first = destinations[0];
        last = 'RTMP destinations';
      } else {
        first = destinations[0];
        last = destinations[1];
      }
      destinations = `${first} and ${last}`;
    } else {
      let last;
      if (_.contains(destinations, 'an RTMP destination')) {
        destinations = _.without(destinations, 'an RTMP destination');
        last = 'an RTMP destination';
      } else if (_.contains(destinations, 'RTMP destinations')) {
        last = 'RTMP destinations';
      } else {
        last = destinations.pop();
      }
      destinations = `${destinations.join(', ')}, and ${last}`;
    }
    return `This broadcast ${beVerb} streamed to ${destinations}.`;
  };

  this.openLowLatencyPreview = () => {
    this.onOpenLowLatencyPreview.bind(this.parentVm)();
  };
}

export default playerWithHighlightCapture;



// WEBPACK FOOTER //
// ./app/pages/events/player-with-highlight-capture.js